import React, { useState, useEffect } from 'react';
import { Mail, Clock, CheckCircle2, MessageCircle, ChevronDown, ChevronUp, CheckCheck } from 'lucide-react';
import axiosClient from '../api/axiosClient';
import ClientChatThread from '../components/client/ClientChatThread';

export default function ClientMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await axiosClient.get('/trials/me/messages');
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error('Failed to load messages', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await axiosClient.put(`/trials/me/messages/${id}/read`);
      // Update local state to reflect it's read
      setMessages(prev => prev.map(m => m.id === id ? { ...m, client_read: 1 } : m));
      // Dispatch events so Navbar updates its badge
      window.dispatchEvent(new Event('auth-change'));
      window.dispatchEvent(new Event('messages-updated'));
      // Close expanded view automatically after marking as read
      setExpandedId(null);
    } catch (err) {
      console.error('Failed to mark read', err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">My Messages</h1>
          <p className="text-slate-600">Track your submitted enquiries and our support team's responses.</p>
        </div>

        <div className="space-y-4">
          {loading ? (
            <div className="p-12 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">
              Loading messages...
            </div>
          ) : messages.length === 0 ? (
            <div className="p-12 text-center text-slate-500 bg-white rounded-2xl border border-slate-200 flex flex-col items-center">
              <MessageCircle size={48} className="text-slate-200 mb-4" />
              <p className="font-medium text-slate-700">No messages yet.</p>
              <p className="text-sm mt-1">When you submit a contact form, it will appear here.</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div 
                key={msg.id} 
                className={`bg-white rounded-2xl border transition-all duration-200 shadow-sm hover:shadow-md ${!msg.client_read ? 'border-primary-300 ring-1 ring-primary-100' : 'border-slate-200'}`}
              >
                <div 
                  className="p-5 cursor-pointer flex items-start gap-4"
                  onClick={() => handleExpand(msg.id)}
                >
                  <div className={`mt-1 p-2 rounded-full ${!msg.client_read ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-500'}`}>
                    <Mail size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className={`font-bold ${!msg.client_read ? 'text-slate-900' : 'text-slate-700'}`}>
                        {msg.subject}
                      </h3>
                      <span className="text-xs font-medium text-slate-500 whitespace-nowrap ml-4">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div className="mt-3 flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        msg.status === 'unread' ? 'bg-amber-100 text-amber-700' :
                        msg.status === 'read' ? 'bg-blue-100 text-blue-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {msg.status === 'unread' ? <Clock size={12} /> : msg.status === 'read' ? <Mail size={12} /> : <CheckCircle2 size={12} />}
                        <span className="capitalize">{msg.status === 'unread' ? 'Pending Reply' : 'Replied'}</span>
                      </span>
                      
                      {!msg.client_read && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-600">
                          New Reply
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-slate-400 self-center">
                    {expandedId === msg.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedId === msg.id && (
                  <div className="border-t border-slate-100 p-5 bg-slate-50 rounded-b-2xl animate-in slide-in-from-top-2 duration-200">
                    <ClientChatThread enquiry={msg} onMarkAsRead={handleMarkAsRead} />
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
